import Stack from "@mui/material/Stack";
import JumboAppTextField from "@jumbo/components/JumboAppTextField";
import LoadingButton from "@mui/lab/LoadingButton";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import {
  Autocomplete,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { stateList } from "@jumbo/constants/stateConst";

const AddVendor = ({ value, onClose, handleSubmit }) => {
  const mobileView = useMediaQuery("(max-width:575px)");

  const validationSchema = Yup.object({
    name: Yup.string()
      .min(3, "Name must be at least 3 characters")
      .max(50, "Name must be at most 50 characters")
      .required("Please enter name"),
    phone: Yup.string().matches(
      /^(?:(?:\+[1-9]{1,4}[ \-]*)|(?:\([0-9]{2,3}\)[ \-]*)|(?:[0-9]{2,4}[ \-]*))*(?:\+[0-9]{1,4}[ \-]*)?[0-9]{3,4}[ \-]*[0-9]{3,4}$/,
      "Mobile number is not valid"
    ),
    gstNumber: Yup.string().matches(
      /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
      "GST number is not valid"
    ),
    panNumber: Yup.string().matches(
      /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
      "PAN number is not valid"
    ),
    state: Yup.string().when("gstNumber", {
      is: (val) => val && val.trim() !== "",
      then: (schema) => schema.required("State is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  return (
    <>
      <Formik
        initialValues={{
          createFor: value?.createFor || [],
          name: value?.name || "",
          phone: value?.phone || "",
          address: value?.address || "",
          gstNumber: value?.gstNumber || "",
          email: value?.email || "",
          city: value?.city || "",
          state: value?.state || "",
          pinCode: value?.pinCode || "",
          panNumber: value?.panNumber || "",
        }}
        validationSchema={validationSchema}
        onSubmit={(data, { setSubmitting }) => {
          setSubmitting(true);
          setTimeout(() => {
            handleSubmit(data);
            setSubmitting(false);
          }, 1500);
        }}
      >
        {({
          isSubmitting,
          touched,
          errors,
          values,
          handleChange,
          handleBlur,
          setFieldValue,
        }) => (
          <Form>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  error={touched.createFor && Boolean(errors.createFor)}
                >
                  <InputLabel id="createFor-label">Create For</InputLabel>
                  <Select
                    multiple
                    name="createFor"
                    labelId="createFor-label"
                    id="createFor"
                    label="Create For"
                    value={values.createFor || []}
                    onChange={(e) => {
                      const { value } = e.target;
                      setFieldValue(
                        "createFor",
                        typeof value === "string" ? value.split(",") : value
                      );
                      setFieldValue("userId", null);
                    }}
                    onBlur={handleBlur}
                    renderValue={(selected) => selected.join(", ")}
                  >
                    <MenuItem value="Fabric">Fabric</MenuItem>
                    <MenuItem value="Stitch">Stitch</MenuItem>
                    <MenuItem value="Modeling">Modeling</MenuItem>
                    <MenuItem value="ReadyMade">ReadyMade</MenuItem>
                    <MenuItem value="Dyer">Dyer</MenuItem>
                  </Select>
                  {touched.createFor && errors.createFor && (
                    <FormHelperText>{errors.createFor}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <JumboAppTextField fullWidth name="name" label="Name*" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <JumboAppTextField fullWidth name="phone" label="Phone" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <JumboAppTextField fullWidth name="email" label="Email" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="gstNumber"
                  label="GST NO"
                  onChange={(e) => {
                    e.target.value = e.target.value.toUpperCase();
                    setFieldValue("gstNumber", e.target.value);
                  }}
                  value={values.gstNumber}
                  error={touched.gstNumber && Boolean(errors.gstNumber)}
                  helperText={touched.gstNumber && errors.gstNumber}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="panNumber"
                  label="PAN NO"
                  onChange={(e) => {
                    e.target.value = e.target.value.toUpperCase();
                    setFieldValue("panNumber", e.target.value);
                  }}
                  value={values.panNumber}
                  error={touched.panNumber && Boolean(errors.panNumber)}
                  helperText={touched.panNumber && errors.panNumber}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <JumboAppTextField fullWidth name="city" label="City" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  id="state"
                  name="state"
                  options={stateList ?? []}
                  getOptionLabel={(option) => option?.name}
                  defaultValue={stateList?.find(
                    (item) => item?._id === values?.state
                  )}
                  openOnFocus
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="State"
                      placeholder="Select a State"
                      error={touched?.state && Boolean(errors?.state)}
                      helperText={touched?.state && errors?.state}
                    />
                  )}
                  onBlur={async (e) => {
                    handleBlur(e);
                  }}
                  onChange={(evt, value) => {
                    setFieldValue("state", value?._id);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <JumboAppTextField
                  fullWidth
                  type="number"
                  name="pinCode"
                  label="PinCode"
                />
              </Grid>
              <Grid item xs={12} sm={12} mb={2}>
                <JumboAppTextField fullWidth name="address" label="Address" />
              </Grid>
            </Grid>
            <Stack
              spacing={{ xs: 1, sm: 2 }}
              direction="row"
              justifyContent="flex-end"
            >
              <LoadingButton
                variant="outlined"
                color="secondary"
                onClick={onClose}
                size="large"
                fullWidth={mobileView}
              >
                Cancel
              </LoadingButton>
              <LoadingButton
                type="submit"
                variant="contained"
                size="large"
                sx={{ mb: 3 }}
                loading={isSubmitting}
                loadingIndicator={<CircularProgress size={20} />}
                fullWidth={mobileView}
              >
                Save
              </LoadingButton>
            </Stack>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default AddVendor;
